import { Button, TextField, cn } from '@simplywallst/ui-core/next';
import AiSolid from '@simplywallst/ui-core/icons/AiSolid';
import { useNarrativeTitleSuggestions } from '@/pages/Narrative/hooks/useNarrativeTitleSuggestions';
import { NarrativeForm } from '@/pages/Narrative/utils/schema';
import { useCallback } from 'react';
import { useTracking } from 'react-tracking';
import { useSnackbar } from '@/features/snackbar';
import { useFlag } from '@simplywallst/unleash';
import useUser from '@/hooks/useUser';

type Props = {
  values: NarrativeForm;
  onChange: (title: string) => void;
  isTouched: boolean;
  error: string | null | undefined;
  companyId: string;
};

export const NarrativeMiniValuatorTitle = ({
  values,
  companyId,
  onChange,
  isTouched,
  error,
}: Props) => {
  const { trackEvent } = useTracking({ subject: 'title' });
  const { addSnackbar } = useSnackbar();
  const { data: user } = useUser();
  const { isLoading: isGenerating, nextSuggestion } =
    useNarrativeTitleSuggestions({
      companyId,
      contentHtml: values.content.html,
      title: values.title,
      fairValue: values.fairValue,
      discountRate: values.discountRatePercentage,
      futureSharesOutstanding: values.futureSharesOutstanding,
      futurePePercentageChange: values.priceEarningsMultiple,
      profitMarginPercentageChange: values.profitMarginPercentage,
      revenuePercentageChange: values.revenuePercentage,
      timeframe: values.timePeriod,
    });

  const handleOnGenerate = useCallback(async () => {
    try {
      const title = await nextSuggestion();
      trackEvent({ action: 'generate', old: values.title, new: title });
      onChange(title);
    } catch (err) {
      console.error('Failed to generate AI title', err);
      addSnackbar({
        message: 'Failed to generate a title',
        type: 'negative',
        lifeTime: 2000,
      });
    }
  }, [onChange, nextSuggestion, addSnackbar, values.title, trackEvent]);

  const { enabled, ready } = useFlag('mono-narratives-ai-title');

  const hasAITitlesEnabled = enabled && ready && user.loggedIn;

  return (
    <div
      className={cn(
        'mt-x1 overflow-y-clip transition-[max-height,min-height,opacity] duration-500',
        isTouched || values.title
          ? 'max-h-x11 min-h-x9 opacity-100'
          : 'max-h-0 opacity-0'
      )}
    >
      <TextField
        label="Add summary"
        placeholder="What's your take on this stock?"
        value={values.title}
        onChange={(value) => onChange(value || '')}
        error={error}
        isDisabled={isGenerating}
        isLoading={isGenerating}
        postfix={
          hasAITitlesEnabled && (
            <Button
              onClick={handleOnGenerate}
              isDisabled={isGenerating}
              size="medium"
              variant="subtle"
              className={cn(
                'relative left-x0_75 p-x0_5 transition-opacity',
                isGenerating ? 'opacity-0' : 'opacity-100'
              )}
              aria-label="Generate title"
            >
              <AiSolid className={cn(isGenerating && 'animate-pulse')} />
            </Button>
          )
        }
      />
    </div>
  );
};
